import { UserRole, AssistantType } from "@speakai/shared";
import type { PersonalizationContext } from "@/hooks/use-personalization-context";

export type ApolloPersona =
  | "QUALITATIVE_INSIGHT"
  | "MEDIA_INTENSIVE"
  | "PROFESSIONAL_BUYER"
  | "SALES"
  | "DEFAULT";

export type NarrativePersona = ApolloPersona | "TEAM_MANAGER" | "SOLO_CREATOR";

/**
 * Extends getPersonaBucket with role awareness for narrative insight copy routing.
 * role=null → SOLO_CREATOR. role=OWNER|ADMIN → TEAM_MANAGER. role=MEMBER falls through.
 */
export function getNarrativePersona(
  ctx: Pick<
    import("@/hooks/use-personalization-context").PersonalizationContext,
    | "apolloEnriched"
    | "apolloIndustry"
    | "apolloSeniority"
    | "apolloDepartments"
    | "apolloTitle"
    | "onboardingGoal"
    | "jobTitle"
    | "role"
  >,
): NarrativePersona {
  if (ctx.role === null) return "SOLO_CREATOR";
  if (ctx.role === UserRole.OWNER || ctx.role === UserRole.ADMIN) return "TEAM_MANAGER";
  return getPersonaBucket(ctx);
}

const QUALITATIVE_INDUSTRIES = [
  "legal",
  "law",
  "research",
  "consulting",
  "education",
  "academic",
  "policy",
  "compliance",
];
const QUALITATIVE_GOALS = [
  "research",
  "legal",
  "interview",
  "consulting",
  "education",
  "academic",
  "survey",
  "form",
];
const MEDIA_INDUSTRIES = [
  "media",
  "production",
  "broadcast",
  "entertainment",
  "journalism",
  "podcast",
  "film",
  "television",
  "radio",
  "music",
  "publishing",
];
const MEDIA_GOALS = [
  "content",
  "media",
  "podcast",
  "recording",
  "broadcast",
  "video",
];
const SENIOR_SENIORITIES = new Set(["director", "vp", "c_suite", "owner"]);
const SENIOR_TITLE_KEYWORDS = [
  "director",
  "vp",
  "vice president",
  "ceo",
  "cto",
  "coo",
  "cfo",
  "chief",
  "owner",
  "president",
  "founder",
  "partner",
];
const SALES_DEPARTMENTS = [
  "sales",
  "business development",
  "revenue operations",
  "account management",
  "bdr",
  "sdr",
];
const SALES_TITLE_KEYWORDS = [
  "sales leader",
  "sales trainer",
  "sales coach",
  "account executive",
  "account manager",
  "sdr",
  "bdr",
  "sales representative",
  "sales rep",
  "sales development",
  "business development rep",
  "business development manager",
  "inside sales",
  "outside sales",
];
// "phone"/"call" match the legacy "Phone Call Transcription & Analysis" value;
// "sales" matches the canonical 3-bucket "Sales" goal. No collisions with other values.
const SALES_GOALS = ["phone", "call", "sales"];

/**
 * Derives a persona bucket from available personalization signals.
 * Apollo fields used only when apolloEnriched === true; onboarding signals are fallback.
 * Returns DEFAULT for all-null inputs — never throws.
 */
export function getPersonaBucket(
  ctx: Pick<
    PersonalizationContext,
    | "apolloEnriched"
    | "apolloIndustry"
    | "apolloSeniority"
    | "apolloDepartments"
    | "apolloTitle"
    | "onboardingGoal"
    | "jobTitle"
  >,
): ApolloPersona {
  const industry = ctx.apolloIndustry?.toLowerCase() ?? "";
  const goal = ctx.onboardingGoal?.toLowerCase() ?? "";
  const seniority = ctx.apolloSeniority?.toLowerCase() ?? "";
  const jobTitleLower = ctx.jobTitle?.toLowerCase() ?? "";

  // Apollo seniority/jobTitle signals with qualitative-context carve-out.
  // Seniority/title gate fires first for most cases (VP/Owner/C-suite → PROFESSIONAL_BUYER).
  // Exception: when any qualitative signal is present (industry OR "research" in title), route
  // QUALITATIVE_INSIGHT instead. "Qualitative" = legal, consulting, academic, research, policy, etc.
  // Examples:
  //   "Sales Director" (no qualitative signal) → PROFESSIONAL_BUYER ✓
  //   "Director of Research" (/\bresearch\b/ matches title) → QUALITATIVE_INSIGHT ✓
  //   "VP of Research" (title match) → QUALITATIVE_INSIGHT ✓
  //   "CEO, Legal Consulting" (apolloEnriched=true, industry in QUALITATIVE_INDUSTRIES) → QUALITATIVE_INSIGHT ✓
  //   "VP of Sales at Deloitte" (enriched, industry="consulting") → QUALITATIVE_INSIGHT (intentional:
  //     qualitative industry overrides sales seniority signal for enriched users)
  //   Non-enriched CEO at consulting firm → industry="" (apolloIndustry only populated when enriched);
  //     relies on /\bresearch\b/ title match only. "Consulting" alone is insufficient for non-enriched.
  // Applies to both Apollo-enriched and self-reported paths.
  const isQualitativeContext =
    QUALITATIVE_INDUSTRIES.some((i) => industry.includes(i)) ||
    /\bresearch\b/i.test(ctx.apolloTitle ?? "") ||
    /\bresearch\b/i.test(jobTitleLower);

  if (ctx.apolloEnriched && SENIOR_SENIORITIES.has(seniority)) {
    if (isQualitativeContext) return "QUALITATIVE_INSIGHT";
    return "PROFESSIONAL_BUYER";
  }
  if (
    !ctx.apolloEnriched &&
    SENIOR_TITLE_KEYWORDS.some((k) => jobTitleLower.includes(k))
  ) {
    if (isQualitativeContext) return "QUALITATIVE_INSIGHT";
    return "PROFESSIONAL_BUYER";
  }

  // Apollo departments/title first; onboardingGoal/jobTitle as fallback.
  // BDR/SDR/AE → SALES (senior sales leaders already captured by PROFESSIONAL_BUYER above).
  if (ctx.apolloEnriched) {
    const depts = ctx.apolloDepartments?.toLowerCase() ?? "";
    const apolloTitleLower = ctx.apolloTitle?.toLowerCase() ?? "";
    if (
      SALES_DEPARTMENTS.some((d) => depts.includes(d)) ||
      SALES_TITLE_KEYWORDS.some((k) => apolloTitleLower.includes(k))
    ) {
      return "SALES";
    }
  }
  // Goal/jobTitle fallback runs for both enriched and non-enriched users.
  // Onboarding goal ("phone call") is a valid SALES signal even when Apollo data is present.
  // Unlike PROFESSIONAL_BUYER, Apollo seniority is not the sole gate for SALES.
  if (
    SALES_GOALS.some((g) => goal.includes(g)) ||
    SALES_TITLE_KEYWORDS.some((k) => jobTitleLower.includes(k))
  ) {
    return "SALES";
  }

  // Apollo industry first; onboardingGoal as fallback
  if (
    ctx.apolloEnriched &&
    QUALITATIVE_INDUSTRIES.some((i) => industry.includes(i))
  ) {
    return "QUALITATIVE_INSIGHT";
  }
  if (QUALITATIVE_GOALS.some((g) => goal.includes(g)))
    return "QUALITATIVE_INSIGHT";

  // Apollo industry first; onboardingGoal as fallback
  if (
    ctx.apolloEnriched &&
    MEDIA_INDUSTRIES.some((i) => industry.includes(i))
  ) {
    return "MEDIA_INTENSIVE";
  }
  if (MEDIA_GOALS.some((g) => goal.includes(g))) return "MEDIA_INTENSIVE";

  return "DEFAULT";
}

/**
 * Maps a persona bucket to the chat assistant preset used by default.
 * RECRUITER is intentionally absent — no persona signal maps to it today;
 * users can still pick it manually. PROFESSIONAL_BUYER + DEFAULT use the
 * strong GENERAL assistant (summaries / decisions / action items).
 */
export const PERSONA_ASSISTANT_TYPE: Record<ApolloPersona, AssistantType> = {
  QUALITATIVE_INSIGHT: AssistantType.RESEARCHER,
  MEDIA_INTENSIVE: AssistantType.MARKETER,
  SALES: AssistantType.SALES,
  PROFESSIONAL_BUYER: AssistantType.GENERAL,
  DEFAULT: AssistantType.GENERAL,
};
